/**
 * Planning an export of the Edit page's timeline: which frames show which clips, which sound
 * is heard, at what size and bitrate. Kept apart from the encoder, which is loaded only when
 * an export starts.
 */
import { activeVideoLayers, clipDuration, clipGainDb, dbToGain, trackLevel, type QuickEditorProject } from './quickEditor';

export type ExportQuality = 'standard' | 'high' | 'best';

export interface ExportSettings {
  /** The part of the timeline to export, in seconds. */
  startSec: number;
  endSec: number;
  width: number;
  height: number;
  fps: number;
  quality: ExportQuality;
}

export type ExportStage = 'preparing' | 'sound' | 'picture' | 'finishing';

export interface ExportProgress {
  stage: ExportStage;
  /** How far the whole export is, 0 to 1. */
  fraction: number;
}

/** Video bitrate at 1080p and 30 frames a second, per quality; other sizes and rates are scaled from it. */
const VIDEO_MBPS_1080P30: Record<ExportQuality, number> = { standard: 4, high: 10, best: 20 };
export const AUDIO_KBPS: Record<ExportQuality, number> = { standard: 192, high: 192, best: 256 };

/**
 * The video bitrate for a size, frame rate and quality: the 1080p rate scaled a little less
 * than the number of pixels, and a little less than the frame rate (detail between frames repeats).
 */
export const videoBitrate = (settings: Pick<ExportSettings, 'width' | 'height' | 'fps' | 'quality'>): number => {
  const pixels = Math.pow((settings.width * settings.height) / (1920 * 1080), 0.95);
  const frames = Math.pow(settings.fps / 30, 0.75);
  return Math.max(1_000_000, Math.round(VIDEO_MBPS_1080P30[settings.quality] * 1_000_000 * pixels * frames));
};
export const frameCount = (settings: Pick<ExportSettings, 'startSec' | 'endSec' | 'fps'>): number =>
  Math.max(1, Math.round((settings.endSec - settings.startSec) * settings.fps));

/** One frame's layers, bottom first: which clip, at what time in its media, how opaque. */
export interface PlannedLayer {
  clipId: string;
  assetId: string;
  sourceSec: number;
  opacity: number;
}

/**
 * The layers of every frame. Frame i shows the edit as it is in the middle of that frame,
 * start + (i + ½) / fps: the clip there, and the frame of its media there, are the ones on
 * screen for the whole frame. Times on the frame grid are kept to the millisecond, so a cut
 * or a source frame can sit a fraction of a millisecond off a frame start; the middle of the
 * frame is never in doubt.
 */
export const planFrames = (project: QuickEditorProject, settings: Pick<ExportSettings, 'startSec' | 'endSec' | 'fps'>): PlannedLayer[][] =>
  Array.from({ length: frameCount(settings) }, (_, i) => {
    const time = settings.startSec + (i + 0.5) / settings.fps;
    return activeVideoLayers(project, time).map(({ clip, opacity }) => ({
      clipId: clip.id, assetId: clip.assetId, sourceSec: clip.inSec + (time - clip.startSec), opacity,
    }));
  });

/** One clip's sound within the export: the part of its media, where it starts in the export, and how loud. */
export interface PlannedSound {
  clipId: string;
  assetId: string;
  fromSec: number;
  toSec: number;
  atSec: number;
  gain: number;
}

/** The sound heard within the range: clips on audio tracks that are not muted, at their volume. */
export const planSound = (project: QuickEditorProject, settings: Pick<ExportSettings, 'startSec' | 'endSec'>): PlannedSound[] => {
  const sounds: PlannedSound[] = [];
  for (const clip of project.clips) {
    const track = project.tracks.find(t => t.id === clip.trackId);
    if (!track || track.kind !== 'audio' || track.muted) continue;
    const gain = trackLevel(track) * dbToGain(clipGainDb(clip));
    if (gain <= 0) continue;
    const from = Math.max(clip.startSec, settings.startSec);
    const to = Math.min(clip.startSec + clipDuration(clip), settings.endSec);
    if (to - from <= 1e-6) continue;
    sounds.push({
      clipId: clip.id, assetId: clip.assetId,
      fromSec: clip.inSec + (from - clip.startSec), toSec: clip.inSec + (to - clip.startSec),
      atSec: from - settings.startSec, gain,
    });
  }
  return sounds;
};

/** Sizes to offer for an edit whose main video is `width` × `height`: as it is, then smaller ones of the same shape. */
export const exportSizes = (width: number, height: number): { width: number; height: number; label: string }[] => {
  const even = (n: number) => Math.max(2, Math.round(n / 2) * 2);
  const shortSide = Math.min(width, height);
  const sizes = [{ width: even(width), height: even(height), label: 'Original' }];
  for (const target of [1080, 720]) {
    if (target >= shortSide) continue;
    const scale = target / shortSide;
    sizes.push({ width: even(width * scale), height: even(height * scale), label: `${target}p` });
  }
  return sizes;
};
